import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  Grid,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Avatar,
  Chip,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  AdminPanelSettings as AdminIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  PointOfSale as PosIcon,
  Inventory as InventoryIcon,
  AccountBalance as AccountingIcon,
  DoNotDisturb as NoModulesIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/hooks/useAuth";
import {
  userManagementService,
  Role,
} from "../../../../services/userManagement.service";
import { getUserAdminModules } from "../../../auth/utils/permission.utils";
import {
  publicService,
  Location,
  LocationsResponse,
} from "../../../../services/public.service";
import { staffService } from "../../services/staff.service";
import {
  configService,
  UpdateUserPermissionsRequest,
  UserPermissionsUpdate,
} from "../../services/config.service";
import { IESDoc } from "../../../../app.interface";
import { toast } from "react-toastify";
import {
  ModuleAdmin,
  ModuleAdmins,
  ModuleConfig,
  UserPermission,
  UserRole,
  ExtendedUser,
  SystemPermission,
  UserNewPermissions,
  UserNewRole,
  LocationPermission,
} from "./UserManagement.interface";
import { PermissionsMatrix } from "./PermissionsMatrix";
import { toLower } from "lodash";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const EmptyModulesState: React.FC = () => {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        bgcolor: "background.paper",
        p: 8,
        textAlign: "center",
      }}
    >
      <Stack spacing={3} alignItems="center">
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            bgcolor: "grey.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <NoModulesIcon
            sx={{
              fontSize: 40,
              color: "grey.400",
            }}
          />
        </Box>

        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              mb: 1,
            }}
          >
            No Active Modules
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              maxWidth: 500,
              mx: "auto",
            }}
          >
            This organization doesn't have any active modules configured for
            user management. Please contact your system administrator to enable
            modules.
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            Available modules include:
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            flexWrap="wrap"
          >
            {moduleConfigs.map((module) => (
              <Chip
                key={module.key}
                label={module.name}
                variant="outlined"
                size="small"
                icon={<Box sx={{ color: module.color }}>{module.icon}</Box>}
                sx={{
                  borderColor: "grey.300",
                  color: "text.secondary",
                }}
              />
            ))}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
};

const moduleConfigs: ModuleConfig[] = [
  {
    key: "hr",
    name: "Human Resources",
    icon: <PersonIcon />,
    color: "#9B2F5D",
    description: "Manage staff, payroll, and HR operations",
  },
  {
    key: "accounting",
    name: "Accounting",
    icon: <AccountingIcon />,
    color: "#7F79A2",
    description: "Financial records and transaction management",
  },
  {
    key: "inventory",
    name: "Inventory",
    icon: <InventoryIcon />,
    color: "#06B6D4",
    description: "Stock management and inventory control",
  },
  {
    key: "pos",
    name: "Point of Sale",
    icon: <PosIcon />,
    color: "#31B5A9",
    description: "Sales operations and customer transactions",
  },
];

// Mock data for permissions matrix

// Permissions Matrix Component

export const UserManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [moduleAdmins, setModuleAdmins] = useState<ModuleAdmins>({});
  const [staffMembers, setStaffMembers] = useState<IESDoc[]>([]);
  const [availableModules, setAvailableModules] = useState<ModuleConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Access control state
  const [userAdminModules, setUserAdminModules] = useState<string[]>([]);
  const [hasPageAccess, setHasPageAccess] = useState(false);
  const [accessLoading, setAccessLoading] = useState(true);

  // Permissions matrix state
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const [matrixTab, setMatrixTab] = useState<{ [moduleKey: string]: number }>(
    {}
  );
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);

  // Real data state
  const [users, setUsers] = useState<ExtendedUser[]>([]);
  const [locations, setLocations] = useState<LocationsResponse | null>(null);
  const [allLocations, setAllLocations] = useState<Location[]>([]);
  const [systemPermissions, setSystemPermissions] = useState<{
    [module: string]: SystemPermission[];
  }>({});
  const [roles, setRoles] = useState<{ [module: string]: any[] }>({});
  const [pendingChanges, setPendingChanges] = useState<{
    [userId: string]: {
      newPermissions?: UserNewPermissions;
      newRoles?: UserNewRole[];
    };
  }>({});

  // Helper function to map module names between component and API
  const getAPIModuleName = (moduleKey: string): string => {
    const moduleMap: { [key: string]: string } = {
      hr: "HR",
      accounting: "Accounting",
      inventory: "Inventory",
      pos: "POS",
    };
    return moduleMap[moduleKey] || moduleKey;
  };

  const getComponentModuleName = (apiModuleName: string): string => {
    const moduleMap: { [key: string]: string } = {
      HR: "hr",
      Accounting: "accounting",
      Inventory: "inventory",
      POS: "pos",
    };
    return moduleMap[apiModuleName] || apiModuleName.toLowerCase();
  };

  // Helper function to convert API permission to user permission format
  const convertAPIPermissionToUserFormat = (
    apiPermission: string,
    moduleKey: string
  ): string => {
    const modulePrefix = `${moduleKey.toLowerCase()}.`;
    return apiPermission.startsWith(modulePrefix)
      ? apiPermission.substring(modulePrefix.length)
      : apiPermission;
  };

  // Helper function to convert user permission to API format
  const convertUserPermissionToAPIFormat = (
    userPermission: string,
    moduleKey: string
  ): string => {
    return `${moduleKey.toLowerCase()}.${userPermission}`;
  };

  // Helper function to save permissions to localStorage
  const savePermissionsToStorage = (permissions: {
    [userId: string]: {
      newPermissions?: UserNewPermissions;
      newRoles?: UserNewRole[];
    };
  }) => {
    try {
      const storageKey = `userPermissions_${user?.store_id}`;
      localStorage.setItem(storageKey, JSON.stringify(permissions));
    } catch (error) {
      console.error("Failed to save permissions to localStorage:", error);
    }
  };

  // Helper function to load permissions from localStorage
  const loadPermissionsFromStorage = (): {
    [userId: string]: {
      newPermissions?: UserNewPermissions;
      newRoles?: UserNewRole[];
    };
  } => {
    try {
      const storageKey = `userPermissions_${user?.store_id}`;
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error("Failed to load permissions from localStorage:", error);
      return {};
    }
  };

  // Helper function to merge user data with stored permissions
  const mergeUserDataWithStoredPermissions = (
    users: ExtendedUser[]
  ): ExtendedUser[] => {
    const storedPermissions = loadPermissionsFromStorage();

    return users.map((user) => {
      const userStoredData = storedPermissions[user._id];
      return {
        ...user,
        newPermissions: userStoredData?.newPermissions || {},
        newRoles: userStoredData?.newRoles || [],
      };
    });
  };

  // Check user access to this page
  const checkUserAccess = async () => {
    if (!user?._id || !user?.store_id) return;

    setAccessLoading(true);
    try {
      const adminModules = await getUserAdminModules(
        user._id,
        user.store_id,
        user.is_store_owner
      );

      setUserAdminModules(adminModules);

      // User has access if they have at least one admin module
      const hasAccess = adminModules.length > 0;
      setHasPageAccess(hasAccess);

      if (!hasAccess) {
        toast.error("You don't have permission to access User Management");
      }
    } catch (error) {
      console.error("Error checking user access:", error);
      setHasPageAccess(false);
      toast.error("Failed to verify access permissions");
    } finally {
      setAccessLoading(false);
    }
  };

  const fetchData = async () => {
    if (!user?.store_id) return;

    setLoading(true);

    try {
      // Load all data in parallel
      const [adminsResponse, staffResponse, usersResponse, locationsResponse] =
        await Promise.all([
          userManagementService.getModuleAdmins(user.store_id),
          staffService.listStaffMembers({}),
          publicService.getUsersByStoreId(user.store_id, {
            page: 1,
            limit: 1000, // Get all users
            is_active: true,
          }),
          publicService.getLocations(user.store_id),
        ]);

      // Process module admins
      if (adminsResponse.success) {
        setModuleAdmins(adminsResponse.data);

        // Determine available modules based on the response and user's admin permissions
        const responseModules = adminsResponse.data;
        const available = moduleConfigs.filter((module) => {
          // Check if the module exists in the response (even if null/undefined)
          const moduleExistsInResponse = responseModules.hasOwnProperty(
            module.key
          );

          // Check if user has admin access to this module
          const userHasAccess = userAdminModules.includes(module.key);

          return moduleExistsInResponse && userHasAccess;
        });

        setAvailableModules(available);

        // Reset active tab if current tab is not available
        if (available.length > 0 && activeTab >= available.length) {
          setActiveTab(0);
        }

        // Load permissions and roles for each available module
        const permissionPromises = available.map(async (module) => {
          const apiModuleName = getAPIModuleName(module.key);
          try {
            const permissionsResponse = await configService.listPermissions({
              module: toLower(apiModuleName),
              pageSize: 1000, // Get all permissions
            });
            return {
              module: module.key,
              permissions: permissionsResponse.data || [],
            };
          } catch (error) {
            console.error(
              `Error loading permissions for ${module.key}:`,
              error
            );
            return { module: module.key, permissions: [] };
          }
        });

        const rolePromises = available.map(async (module) => {
          const apiModuleName = getAPIModuleName(module.key);
          try {
            const rolesResponse = await userManagementService.listRoles({
              module: toLower(apiModuleName),
              pageSize: 1000, // Get all roles
            });
            return {
              module: module.key,
              roles: rolesResponse.data?.data || [], // Fix: access data.data instead of data.roles
            };
          } catch (error) {
            console.error(`Error loading roles for ${module.key}:`, error);
            return { module: module.key, roles: [] };
          }
        });

        const [permissionResults, roleResults] = await Promise.all([
          Promise.all(permissionPromises),
          Promise.all(rolePromises),
        ]);

        // Process permissions
        const newSystemPermissions: { [module: string]: SystemPermission[] } =
          {};
        permissionResults.forEach((result) => {
          newSystemPermissions[result.module] = result.permissions;
        });
        setSystemPermissions(newSystemPermissions);

        // Process roles - normalize the data to match rolesMatrix component
        const newRoles: { [module: string]: any[] } = {};
        roleResults.forEach((result) => {
          // Normalize roles data to have consistent id and name properties
          const normalizedRoles = result.roles.map((role: any) => ({
            ...role,
            id: role._id || role.id,
            name: role.role_name || role.name,
          }));
          newRoles[result.module] = normalizedRoles;
        });
        setRoles(newRoles);
      } else {
        setAvailableModules([]);
      }

      // Process staff members
      if (staffResponse.data) {
        setStaffMembers(staffResponse.data);
      }

      // Process users
      if (usersResponse.users) {
        const baseUsers: ExtendedUser[] = usersResponse.users.map(
          (userData) => ({
            _id: userData.id,
            email: userData.email,
            first_name: userData.first_name,
            last_name: userData.last_name,
            role: userData.role,
            store_id: user.store_id!,
            is_active: userData.is_active,
            created_at: userData.created_at,
            is_store_owner: false, // Will be populated from actual API
            is_super_admin: false, // Will be populated from actual API
            newPermissions: {}, // Will be merged with stored data
            newRoles: [], // Will be merged with stored data
          })
        );

        // Merge with stored permissions (temporary persistence solution)
        const extendedUsers = mergeUserDataWithStoredPermissions(baseUsers);
        setUsers(extendedUsers);
      }

      // Process locations
      if (locationsResponse) {
        setLocations(locationsResponse);

        // Combine all locations into a single array
        const combinedLocations: Location[] = [
          ...locationsResponse.warehouses,
          ...locationsResponse.branches,
          ...locationsResponse.departments,
        ];
        setAllLocations(combinedLocations);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load user management data");
      setAvailableModules([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user?.store_id) return;

    setSaving(true);

    try {
      const response = await userManagementService.updateModuleAdmins(
        user.store_id,
        moduleAdmins
      );

      if (response.success) {
        toast.success("Module administrators updated successfully");
        setModuleAdmins(response.data);
      }
    } catch (error) {
      console.error("Error updating module admins:", error);
      toast.error("Failed to update module administrators");
    } finally {
      setSaving(false);
    }
  };

  const handleAdminChange = (
    module: keyof ModuleAdmins,
    admin: ModuleAdmin | null
  ) => {
    setModuleAdmins((prev) => ({
      ...prev,
      [module]: admin,
    }));
  };

  const getUserDisplayName = (admin: ModuleAdmin | undefined) => {
    if (!admin) return "Not assigned";
    return admin.name || admin.email;
  };

  const getUserInitials = (admin: ModuleAdmin | undefined) => {
    if (!admin) return "?";
    const name = admin.name || admin.email;
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  // Permissions matrix handlers
  const handleLocationChange = (locationId: string) => {
    setSelectedLocationId(locationId);
  };

  const handleMatrixTabChange = (moduleKey: string, tab: number) => {
    setMatrixTab((prev) => ({ ...prev, [moduleKey]: tab }));
  };

  const handlePermissionToggle = (userId: string, permissionId: string) => {
    console.log("Parent handlePermissionToggle called:", {
      userId,
      permissionId,
      selectedLocationId,
    });
    console.log("Selected Location ID");
    console.log(selectedLocationId);
    console.log("User");
    console.log(user);
    if (!selectedLocationId || !user) return;
    console.log("All Locations");
    console.log(allLocations);
    const selectedLocation = allLocations.find(
      (loc) => loc._id === selectedLocationId
    );
    console.log("Selected Location");
    console.log(selectedLocation);
    if (!selectedLocation) return;

    console.log("Available Modules");
    console.log(availableModules);

    const currentModule = availableModules[activeTab];

    if (!currentModule) return;

    const currentUser = users.find((u) => u._id === userId);
    if (!currentUser) return;

    // Convert permission format from API to user format
    const userPermissionFormat = convertAPIPermissionToUserFormat(
      permissionId,
      currentModule.key
    );

    // Initialize user's newPermissions if it doesn't exist
    const userPermissions = currentUser.newPermissions || {};
    const modulePermissions =
      userPermissions[currentModule.key] || ([] as LocationPermission[]);

    // Find existing location permissions
    const existingLocationIndex = modulePermissions.findIndex(
      (loc) => loc.location_id === selectedLocationId
    );

    let updatedModulePermissions: LocationPermission[];

    if (existingLocationIndex >= 0) {
      // Location permissions exist, toggle the specific permission
      const existingLocation = modulePermissions[existingLocationIndex];
      const hasPermission =
        existingLocation.permissions.includes(userPermissionFormat);

      const updatedPermissions = hasPermission
        ? existingLocation.permissions.filter(
            (p: string) => p !== userPermissionFormat
          )
        : [...existingLocation.permissions, userPermissionFormat];

      const updatedLocationPermissions: LocationPermission = {
        ...existingLocation,
        permissions: updatedPermissions,
        last_modified_by_name: `${user.first_name} ${user.last_name}`.trim(),
        last_modified_by_email: user.email,
        last_modified_at: new Date().toISOString(),
      };

      // Update the specific location in the array
      updatedModulePermissions = [...modulePermissions];
      updatedModulePermissions[existingLocationIndex] =
        updatedLocationPermissions;
    } else {
      // First permission for this location, create new structure
      const newLocationPermissions: LocationPermission = {
        location_id: selectedLocationId,
        id_type: selectedLocation.type,
        branch_name: selectedLocation.name,
        permissions: [userPermissionFormat],
        granted_by_role: user.role || "",
        granted_by_name: `${user.first_name} ${user.last_name}`.trim(),
        granted_by_email: user.email,
        granted_at: new Date().toISOString(),
      };

      // Add the new location to the array
      updatedModulePermissions = [...modulePermissions, newLocationPermissions];
    }

    // Update the user's permissions structure
    const newUserPermissions: UserNewPermissions = {
      ...userPermissions,
      [currentModule.key]: updatedModulePermissions,
    };

    // Update the user in the users array
    const updatedUsers = users.map((u) =>
      u._id === userId ? { ...u, newPermissions: newUserPermissions } : u
    );
    setUsers(updatedUsers);

    // Track pending changes
    const newPendingChanges = {
      ...pendingChanges,
      [userId]: {
        ...pendingChanges[userId],
        newPermissions: newUserPermissions,
      },
    };
    setPendingChanges(newPendingChanges);

    // Save to localStorage for persistence (temporary solution)
    const userPermissionsForStorage = Object.fromEntries(
      updatedUsers.map((u) => [
        u._id,
        {
          newPermissions: u.newPermissions,
          newRoles: u.newRoles,
        },
      ])
    );
    savePermissionsToStorage(userPermissionsForStorage);
  };

  const handleRoleToggle = (userId: string, roleId: string) => {
    console.log("Parent handleRoleToggle called:", {
      userId,
      roleId,
      selectedLocationId,
    });
    if (!selectedLocationId || !user) return;

    const selectedLocation = allLocations.find(
      (loc) => loc._id === selectedLocationId
    );
    if (!selectedLocation) return;

    const currentModule = availableModules[activeTab];
    if (!currentModule) return;

    const currentUser = users.find((u) => u._id === userId);
    if (!currentUser) return;

    // Initialize user's newRoles if it doesn't exist
    const userRoles = currentUser.newRoles || [];

    // Check if this role assignment already exists
    const existingRoleIndex = userRoles.findIndex(
      (r) =>
        r.module === currentModule.key && r.location_id === selectedLocationId
    );

    let updatedRoles: UserNewRole[];

    if (existingRoleIndex >= 0) {
      const existingRole = userRoles[existingRoleIndex];
      if (existingRole.role_id === roleId) {
        // Remove the role if it's the same
        updatedRoles = userRoles.filter(
          (_, index) => index !== existingRoleIndex
        );
      } else {
        // Update with new role
        updatedRoles = [...userRoles];
        updatedRoles[existingRoleIndex] = {
          ...existingRole,
          role_id: roleId,
          assigned_at: new Date().toISOString(),
          assigned_by: {
            name: `${user.first_name} ${user.last_name}`.trim(),
            email: user.email,
          },
        };
      }
    } else {
      // Add new role assignment
      const newRoleAssignment: UserNewRole = {
        module: currentModule.key,
        location_id: selectedLocationId,
        id_type: selectedLocation.type,
        role_id: roleId,
        assigned_at: new Date().toISOString(),
        assigned_by: {
          name: `${user.first_name} ${user.last_name}`.trim(),
          email: user.email,
        },
      };
      updatedRoles = [...userRoles, newRoleAssignment];
    }

    // Update the user in the users array
    const updatedUsers = users.map((u) =>
      u._id === userId ? { ...u, newRoles: updatedRoles } : u
    );
    setUsers(updatedUsers);

    // Track pending changes
    const newPendingChanges = {
      ...pendingChanges,
      [userId]: {
        ...pendingChanges[userId],
        newRoles: updatedRoles,
      },
    };

    setPendingChanges(newPendingChanges);
    console.log("Updated pendingChanges after role toggle:", newPendingChanges);

    // Save to localStorage for persistence (temporary solution)
    const userPermissionsForStorage = Object.fromEntries(
      updatedUsers.map((u) => [
        u._id,
        {
          newPermissions: u.newPermissions,
          newRoles: u.newRoles,
        },
      ])
    );
    savePermissionsToStorage(userPermissionsForStorage);

    // Apply role permissions to user's permissions - REPLACE entirely
    const selectedRole = roles[currentModule.key]?.find(
      (r) => (r._id || r.id) === roleId
    );
    if (selectedRole && selectedRole.permissions) {
      // REPLACE all permissions with role permissions (not add to existing)
      handleRolePermissionsApplication(
        userId,
        selectedRole.permissions,
        currentModule.key,
        updatedRoles // Pass the updated roles directly
      );
    } else if (roleId === null || roleId === "") {
      // If no role is selected, remove all permissions for this module+location
      handleRolePermissionsApplication(
        userId,
        [], // Empty permissions array
        currentModule.key,
        updatedRoles // Pass the updated roles directly
      );
    }
  };

  // Helper function to apply role permissions to user
  const handleRolePermissionsApplication = (
    userId: string,
    rolePermissions: string[],
    moduleKey: string,
    currentUserRoles?: UserNewRole[]
  ) => {
    if (!selectedLocationId || !user) return;

    const selectedLocation = allLocations.find(
      (loc) => loc._id === selectedLocationId
    );
    if (!selectedLocation) return;

    const currentUser = users.find((u) => u._id === userId);
    if (!currentUser) return;

    // Convert API permissions to user format
    const userFormatPermissions = rolePermissions.map((p: string) =>
      convertAPIPermissionToUserFormat(p, moduleKey)
    );

    // Initialize user's newPermissions if it doesn't exist
    const userPermissions = currentUser.newPermissions || {};
    const modulePermissions =
      userPermissions[moduleKey] || ([] as LocationPermission[]);

    // Find existing location permissions
    const existingLocationIndex = modulePermissions.findIndex(
      (loc) => loc.location_id === selectedLocationId
    );

    let updatedModulePermissions: LocationPermission[];

    if (existingLocationIndex >= 0) {
      // Update existing location permissions
      const updatedLocationPermissions: LocationPermission = {
        ...modulePermissions[existingLocationIndex],
        permissions: userFormatPermissions,
        granted_by_role: user.role || "",
        granted_by_name: `${user.first_name} ${user.last_name}`.trim(),
        granted_by_email: user.email,
        granted_at: new Date().toISOString(),
      };

      updatedModulePermissions = [...modulePermissions];
      updatedModulePermissions[existingLocationIndex] =
        updatedLocationPermissions;
    } else {
      // Create new location permissions
      const newLocationPermissions: LocationPermission = {
        location_id: selectedLocationId,
        id_type: selectedLocation.type,
        branch_name: selectedLocation.name,
        permissions: userFormatPermissions,
        granted_by_role: user.role || "",
        granted_by_name: `${user.first_name} ${user.last_name}`.trim(),
        granted_by_email: user.email,
        granted_at: new Date().toISOString(),
      };

      updatedModulePermissions = [...modulePermissions, newLocationPermissions];
    }

    // Update the user's permissions structure - REPLACE entirely for this module
    const newUserPermissions: UserNewPermissions = {
      ...userPermissions,
      [moduleKey]: updatedModulePermissions,
    };

    // Update the user in the users array
    const updatedUsers = users.map((u) =>
      u._id === userId ? { ...u, newPermissions: newUserPermissions } : u
    );
    setUsers(updatedUsers);

    // Track pending changes - PRESERVE existing newRoles that were just set
    const newPendingChanges = {
      ...pendingChanges,
      [userId]: {
        ...pendingChanges[userId],
        newPermissions: newUserPermissions,
        // CRITICAL: Use the passed currentUserRoles or fall back to existing state
        newRoles: currentUserRoles || pendingChanges[userId]?.newRoles || [],
      },
    };
    setPendingChanges(newPendingChanges);

    // Save to localStorage for persistence (temporary solution)
    const userPermissionsForStorage = Object.fromEntries(
      updatedUsers.map((u) => [
        u._id,
        {
          newPermissions: u.newPermissions,
          newRoles: u.newRoles,
        },
      ])
    );
    savePermissionsToStorage(userPermissionsForStorage);
  };

  // Save pending changes - now implements real API calls
  const handleSavePermissionChanges = async () => {
    setSaving(true);

    try {
      const currentModule = availableModules[activeTab];
      if (!currentModule || !user) {
        throw new Error("Invalid module or user context");
      }

      // DEBUG: Check what's in pendingChanges
      console.log("Pending changes being saved:", pendingChanges);

      // Process each user with pending changes
      const savePromises = Object.entries(pendingChanges).map(
        async ([userId, changes]) => {
          const targetUser = users.find((u) => u._id === userId);
          if (!targetUser) {
            throw new Error(`User ${userId} not found`);
          }

          // Prepare the API request data
          const requestData: UpdateUserPermissionsRequest = {
            module: currentModule.key,
            target_user_id: userId,
            newRoles: [],
            newPermissions: {} as UserPermissionsUpdate,
          };

          // Transform newPermissions to API format
          if (changes.newPermissions) {
            Object.entries(changes.newPermissions).forEach(
              ([moduleKey, modulePermissions]) => {
                requestData.newPermissions[moduleKey] = modulePermissions.map(
                  (locationData) => {
                    // Find the location to get its name
                    const location = allLocations.find(
                      (loc) => loc._id === locationData.location_id
                    );

                    // Keep permissions as 2 parts (no module prefix)
                    // User format: "employees.view" -> API format: "employees.view"
                    const apiFormatPermissions = locationData.permissions;

                    const result = {
                      location_id: locationData.location_id,
                      id_type: locationData.id_type,
                      permissions: apiFormatPermissions,
                    };

                    return result;
                  }
                );
              }
            );
          }

          // Transform newRoles to API format (simplified assigned_by)
          if (changes.newRoles) {
            requestData.newRoles = changes.newRoles.map((role) => ({
              module: role.module,
              location_id: role.location_id,
              id_type: role.id_type,
              role_id: role.role_id,
              assigned_at: new Date(role.assigned_at).getTime(),
              assigned_by: user._id, // Use user ID as specified in API
            }));
          }

          // When a role is assigned, replace the permissions with the role's permissions
          if (changes.newRoles && changes.newRoles.length > 0) {
            const roleForCurrentModule = changes.newRoles.find(
              (role) => role.module === currentModule.key
            );
            if (roleForCurrentModule) {
              const assignedRole = roles[currentModule.key]?.find(
                (r) => (r._id || r.id) === roleForCurrentModule.role_id
              );
              if (assignedRole && assignedRole.permissions) {
                // Replace permissions with role permissions
                const rolePermissions = assignedRole.permissions.map(
                  (p: string) =>
                    convertAPIPermissionToUserFormat(p, currentModule.key)
                );

                // Update the permissions structure for this module
                const location = allLocations.find(
                  (loc) => loc._id === roleForCurrentModule.location_id
                );
                if (location) {
                  requestData.newPermissions[currentModule.key] = [
                    {
                      location_id: roleForCurrentModule.location_id,
                      id_type: roleForCurrentModule.id_type,
                      permissions: rolePermissions,
                    },
                  ];
                }
              }
            }
          }

          // Call the API
          const response = await configService.updateUserPermissions(
            requestData
          );

          if (!response.success) {
            throw new Error(
              response.message ||
                `Failed to update permissions for ${targetUser.first_name} ${targetUser.last_name}`
            );
          }

          return {
            userId,
            userName: `${targetUser.first_name} ${targetUser.last_name}`.trim(),
            success: true,
          };
        }
      );

      // Wait for all save operations to complete
      const results = await Promise.all(savePromises);

      // Update users state with the saved data (make it persistent)
      const updatedUsers = users.map((user) => {
        const userChanges = pendingChanges[user._id];
        if (userChanges) {
          return {
            ...user,
            newPermissions: userChanges.newPermissions || user.newPermissions,
            newRoles: userChanges.newRoles || user.newRoles,
          };
        }
        return user;
      });
      setUsers(updatedUsers);

      // Update localStorage with the saved data
      const userPermissionsForStorage = Object.fromEntries(
        updatedUsers.map((u) => [
          u._id,
          {
            newPermissions: u.newPermissions,
            newRoles: u.newRoles,
          },
        ])
      );
      savePermissionsToStorage(userPermissionsForStorage);

      // Clear pending changes on success
      setPendingChanges({});

      const successCount = results.filter((r) => r.success).length;
      toast.success(
        `Successfully updated permissions for ${successCount} user${
          successCount !== 1 ? "s" : ""
        }`
      );
    } catch (error) {
      console.error("Error saving permission changes:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to save permission changes";
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Check user access when user changes
  useEffect(() => {
    checkUserAccess();
  }, [user?._id, user?.store_id]);

  // Fetch data when user has access and admin modules are loaded
  useEffect(() => {
    if (hasPageAccess && userAdminModules.length > 0) {
      fetchData();
    }
  }, [hasPageAccess, userAdminModules, user?.store_id]);

  if (!user) {
    return null;
  }

  // Show loading while checking access
  if (accessLoading) {
    return (
      <Box sx={{ bgcolor: "grey.50", minHeight: "100vh" }}>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              bgcolor: "background.paper",
              p: 8,
              textAlign: "center",
            }}
          >
            <Stack spacing={3} alignItems="center">
              <CircularProgress size={48} />
              <Typography variant="h6" color="text.secondary">
                Checking access permissions...
              </Typography>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Show access denied if user doesn't have permission
  if (!hasPageAccess) {
    return (
      <Box sx={{ bgcolor: "grey.50", minHeight: "100vh" }}>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              bgcolor: "background.paper",
              p: 8,
              textAlign: "center",
            }}
          >
            <Stack spacing={3} alignItems="center">
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: "error.light",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "error.main",
                }}
              >
                <AdminIcon sx={{ fontSize: 40 }} />
              </Box>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    mb: 2,
                  }}
                >
                  Access Denied
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    maxWidth: 500,
                    mx: "auto",
                    mb: 3,
                  }}
                >
                  You don't have permission to access the User Management page.
                  Only store owners and module administrators can access this
                  section.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate("/app-launcher")}
                  sx={{
                    bgcolor: "primary.main",
                    color: "white",
                    "&:hover": {
                      bgcolor: "primary.dark",
                    },
                  }}
                >
                  Back to App Launcher
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "grey.50", minHeight: "100vh" }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 4, py: 2 }}
          >
            <Stack direction="row" alignItems="center" spacing={3}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/app-launcher")}
                sx={{
                  borderColor: "primary.main",
                  color: "primary.main",
                  "&:hover": {
                    bgcolor: "primary.main",
                    color: "white",
                  },
                }}
              >
                Back
              </Button>

              <Stack direction="row" alignItems="center" spacing={2}>
                <AdminIcon
                  sx={{
                    color: "primary.main",
                    fontSize: 28,
                  }}
                />
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      fontSize: { xs: "1.75rem", md: "2.125rem" },
                    }}
                  >
                    User Management
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.875rem",
                      }}
                    >
                      {user.is_store_owner
                        ? "Assign administrators for each module"
                        : `Managing ${userAdminModules.length} module${
                            userAdminModules.length !== 1 ? "s" : ""
                          }`}
                    </Typography>
                    <Chip
                      label={
                        user.is_store_owner ? "Store Owner" : "Module Admin"
                      }
                      size="small"
                      color={user.is_store_owner ? "primary" : "secondary"}
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.75rem",
                      }}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchData}
                disabled={loading}
                sx={{
                  borderColor: "primary.main",
                  color: "primary.main",
                  "&:hover": {
                    bgcolor: "primary.main",
                    color: "white",
                  },
                  "&:disabled": {
                    borderColor: "grey.300",
                    color: "grey.400",
                  },
                }}
              >
                {loading ? "Loading..." : "Refresh"}
              </Button>
            </Stack>
          </Stack>
        </motion.div>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
        >
          {loading ? (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                bgcolor: "background.paper",
                p: 8,
                textAlign: "center",
              }}
            >
              <Stack spacing={3} alignItems="center">
                <CircularProgress size={48} />
                <Typography variant="h6" color="text.secondary">
                  Loading modules...
                </Typography>
              </Stack>
            </Paper>
          ) : availableModules.length === 0 ? (
            <EmptyModulesState />
          ) : (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "background.paper",
              }}
            >
              {/* Modern Tabs */}
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  bgcolor: "grey.50",
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={(_, newValue) => setActiveTab(newValue)}
                  variant="fullWidth"
                  sx={{
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "1rem",
                      minHeight: 72,
                      "&.Mui-selected": {
                        color: "primary.main",
                      },
                    },
                    "& .MuiTabs-indicator": {
                      height: 3,
                      borderRadius: "2px 2px 0 0",
                    },
                  }}
                >
                  {availableModules.map((module, index) => (
                    <Tab
                      key={module.key}
                      label={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ color: module.color }}>{module.icon}</Box>
                          <span>{module.name}</span>
                        </Stack>
                      }
                    />
                  ))}
                </Tabs>
              </Box>

              {/* Tab Content */}
              {availableModules.map((module, index) => (
                <TabPanel key={module.key} value={activeTab} index={index}>
                  <Box sx={{ p: 4 }}>
                    <Stack spacing={4}>
                      {/* Module Header */}
                      <Box>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={2}
                          sx={{ mb: 2 }}
                        >
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              bgcolor: module.color,
                              color: "white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {module.icon}
                          </Box>
                          <Box>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                              {module.name} Administration
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {module.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>

                      <Divider />

                      {/* Module Admin Assignment Section - Only for Store Owners */}
                      {user.is_store_owner && (
                        <>
                          {/* Current Admin Display */}
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 600, mb: 3 }}
                            >
                              Current Administrator
                            </Typography>

                            <Card variant="outlined" sx={{ mb: 4 }}>
                              <CardContent sx={{ p: 3 }}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={3}
                                >
                                  <Avatar
                                    sx={{
                                      width: 56,
                                      height: 56,
                                      bgcolor: moduleAdmins[module.key]
                                        ? module.color
                                        : "grey.300",
                                      fontSize: "1.25rem",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {getUserInitials(moduleAdmins[module.key])}
                                  </Avatar>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography
                                      variant="h6"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {getUserDisplayName(
                                        moduleAdmins[module.key]
                                      )}
                                    </Typography>
                                    {moduleAdmins[module.key] && (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {moduleAdmins[module.key]?.email}
                                      </Typography>
                                    )}
                                    <Chip
                                      label={
                                        moduleAdmins[module.key]
                                          ? "Assigned"
                                          : "Not Assigned"
                                      }
                                      size="small"
                                      color={
                                        moduleAdmins[module.key]
                                          ? "success"
                                          : "default"
                                      }
                                      sx={{ mt: 1 }}
                                    />
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Box>

                          {/* Admin Selection */}
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 600, mb: 3 }}
                            >
                              Assign Administrator
                            </Typography>

                            <Grid container spacing={3}>
                              <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                  <InputLabel>Select Administrator</InputLabel>
                                  <Select
                                    value={
                                      moduleAdmins[module.key]?.user_id || ""
                                    }
                                    onChange={(e) => {
                                      const selectedStaff = staffMembers.find(
                                        (staff) => staff._id === e.target.value
                                      );
                                      if (selectedStaff) {
                                        const name = `${
                                          selectedStaff.first_name || ""
                                        } ${
                                          selectedStaff.last_name || ""
                                        }`.trim();
                                        handleAdminChange(module.key, {
                                          user_id: selectedStaff._id,
                                          name: name || "",
                                          email: selectedStaff.email || "",
                                        });
                                      } else {
                                        handleAdminChange(module.key, null);
                                      }
                                    }}
                                    label="Select Administrator"
                                    disabled={loading}
                                  >
                                    <MenuItem value="">
                                      <em>No administrator assigned</em>
                                    </MenuItem>
                                    {staffMembers.map((staffMember) => {
                                      const name = `${
                                        staffMember.first_name || ""
                                      } ${staffMember.last_name || ""}`.trim();
                                      return (
                                        <MenuItem
                                          key={staffMember._id}
                                          value={staffMember._id}
                                        >
                                          <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={2}
                                          >
                                            <Avatar
                                              sx={{
                                                width: 32,
                                                height: 32,
                                                bgcolor: "primary.main",
                                                fontSize: "0.875rem",
                                              }}
                                            >
                                              {staffMember.name
                                                ?.charAt(0)
                                                ?.toUpperCase() ||
                                                staffMember.email
                                                  ?.charAt(0)
                                                  ?.toUpperCase()}
                                            </Avatar>
                                            <Box>
                                              <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 500 }}
                                              >
                                                {name}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                              >
                                                {staffMember.email} •{" "}
                                                {staffMember.role}
                                              </Typography>
                                            </Box>
                                          </Stack>
                                        </MenuItem>
                                      );
                                    })}
                                  </Select>
                                </FormControl>
                              </Grid>
                            </Grid>
                          </Box>

                          {/* Module Admin Save Button */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              mt: 3,
                            }}
                          >
                            <Button
                              variant="contained"
                              startIcon={
                                saving ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <SaveIcon />
                                )
                              }
                              onClick={handleSave}
                              disabled={saving || loading}
                              sx={{
                                bgcolor: "success.main",
                                color: "white",
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                borderRadius: 2,
                                "&:hover": {
                                  bgcolor: "success.dark",
                                },
                                "&:disabled": {
                                  bgcolor: "grey.300",
                                },
                              }}
                            >
                              {saving
                                ? "Saving Administrator..."
                                : "Save Module Administrator"}
                            </Button>
                          </Box>
                        </>
                      )}

                      <Divider sx={{ my: 4 }} />

                      {/* Permissions & Roles Matrix */}
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, mb: 3 }}
                        >
                          Permissions & Roles Management
                        </Typography>

                        <PermissionsMatrix
                          moduleKey={module.key}
                          moduleColor={module.color}
                          selectedLocationId={selectedLocationId}
                          userPermissions={userPermissions}
                          userRoles={userRoles}
                          onLocationChange={handleLocationChange}
                          onPermissionToggle={handlePermissionToggle}
                          onRoleToggle={handleRoleToggle}
                          activeTab={matrixTab[module.key] || 0}
                          onTabChange={(tab) =>
                            handleMatrixTabChange(module.key, tab)
                          }
                          users={users}
                          locations={allLocations}
                          systemPermissions={
                            systemPermissions[module.key] || []
                          }
                          roles={roles[module.key] || []}
                          saving={saving}
                          pendingChangesCount={
                            Object.keys(pendingChanges).length
                          }
                          onSavePermissionChanges={handleSavePermissionChanges}
                        />
                      </Box>
                    </Stack>
                  </Box>
                </TabPanel>
              ))}
            </Paper>
          )}
        </motion.div>
      </Container>
    </Box>
  );
};
